import { useEffect, useMemo, useRef, useState } from "react";
import { productInput, type ProductDetail, type ProductFormValues, type ProductInput } from "@cx/contracts";

export type TaxValues = NonNullable<ProductFormValues["tax"]>;
export type SupplierValues = NonNullable<ProductFormValues["suppliers"]>[number];

export const EMPTY: ProductFormValues = {
  format: "S",
  kind: "P",
  name: "",
  sku: "",
  status: "active",
  priceCents: 0,
  costCents: 0,
  unit: "UN",
  condition: "unspecified",
  production: "own",
  freeShipping: false,
  dimensionUnit: "cm",
  gtin: "",
  gtinTax: "",
  batchControl: false,
  stockControl: true,
  tax: { origin: 0 },
  suppliers: [],
  components: [],
  inheritParent: true,
  customFields: {},
};

/** Dados de exibição do componente (nome, SKU, unidade, custo), fora do payload. */
/** `costCents` ausente para quem não vê custo (products:read_cost). */
export type ComponentInfo = { name: string; sku: string | null; unit: string; costCents?: number };

/**
 * Converte o detalhe da API em valores do formulário, sem os campos que só o servidor tem. Sem products:read_cost
 * o detalhe vem sem custo: os valores também ficam sem ele (o servidor mantém o custo gravado ao salvar).
 */
export function toFormValues(d: ProductDetail): ProductFormValues {
  const copy: Partial<ProductDetail> = { ...d };
  delete copy.id;
  delete copy.createdAt;
  delete copy.updatedAt;
  delete copy.avgCostCents;
  delete copy.parentId;
  delete copy.parent;
  delete copy.variationValues;
  delete copy.attributes;
  delete copy.variations;
  delete copy.kit;
  const values: ProductFormValues = {
    ...EMPTY,
    ...copy,
    components: d.components.map((c) => ({ componentId: c.componentId, qtyQ4: c.qtyQ4 })),
    suppliers: d.suppliers.map((s) => ({
      supplierId: s.supplierId,
      supplierCode: s.supplierCode,
      supplierDescription: s.supplierDescription,
      ...(s.lastCostCents === undefined ? {} : { lastCostCents: s.lastCostCents }),
      isDefault: s.isDefault,
    })),
  };
  if (d.costCents === undefined) delete values.costCents;
  return values;
}

/** Nomes dos fornecedores do detalhe, para mostrar no formulário sem pôr no payload. */
export const supplierNames = (d: ProductDetail): Record<string, string> =>
  Object.fromEntries(d.suppliers.map((s) => [s.supplierId, s.supplierName]));

/** Nome e custo dos componentes do detalhe, para a aba Composição. */
export const componentInfo = (d: ProductDetail): Record<string, ComponentInfo> =>
  Object.fromEntries(
    d.components.map((c) => [
      c.componentId,
      { name: c.name, sku: c.sku, unit: c.unit, costCents: c.costCents },
    ]),
  );

export function useProductForm(initial: ProductFormValues = EMPTY) {
  const [values, setValues] = useState<ProductFormValues>(initial);
  const [baseline, setBaseline] = useState<ProductFormValues>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const latest = useRef(values);
  useEffect(() => {
    latest.current = values;
  }, [values]);
  const dirty = useMemo(() => JSON.stringify(values) !== JSON.stringify(baseline), [values, baseline]);
  const set = <K extends keyof ProductFormValues>(k: K, v: ProductFormValues[K]) =>
    setValues((x) => ({ ...x, [k]: v }));
  const setTax = (patch: Partial<TaxValues>) => setValues((x) => ({ ...x, tax: { ...x.tax, ...patch } }));
  /** Valida no cliente com o MESMO schema da API; devolve o payload limpo ou null. */
  const validate = (): ProductInput | null => {
    const r = productInput.safeParse(values);
    if (r.success) {
      setErrors({});
      return r.data;
    }
    const e: Record<string, string> = {};
    for (const i of r.error.issues) e[i.path.join(".")] ??= i.message;
    setErrors(e);
    return null;
  };
  /** Depois de salvar, os valores atuais viram a base do "alterado". */
  const markSaved = () => setBaseline(latest.current);
  /** Troca valores e base de uma vez (ex.: filho que herda do pai, recarregado depois de salvar). */
  const reset = (v: ProductFormValues) => {
    setValues(v);
    setBaseline(v);
  };
  return { values, set, setTax, setValues, errors, setErrors, validate, dirty, markSaved, reset };
}
